"use client";

import { useId } from "react";

import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import {
  FACET_SEARCH_THRESHOLD,
  facetSearchPlaceholder,
  searchFacetOptions,
  type FacetKey,
  type FacetOption,
} from "@/lib/host-panel/catalog-facets";

/**
 * A facet's body: its own search field and the option list.
 *
 * Shared by the anchored popover and the bottom sheet, since only the frame changes with width.
 * The registry's `FilterOption` cannot be used: it renders a checkbox without forwarding
 * `checked`/`onChange`, so there is no way to control it (T15).
 */

export type FacetPanelProps = {
  facet: FacetKey;
  options: readonly FacetOption[];
  /** Option count before the search, which decides whether the search appears. */
  totalOptions: number;
  searchTerm: string;
  onSearchChange: (term: string) => void;
  onToggle: (value: string) => void;
  /** Maximum height of the scrollable area; differs between popover and sheet. */
  listClassName: string;
  /**
   * The prototype draws the options twice: tight in the desktop popover (13px rows of 37.4px, a
   * 16px check) and roomy in the phone's bottom sheet (15px rows of 44px, an 18px check).
   */
  density?: "popover" | "sheet";
};

const DENSITY = {
  popover: {
    search: "py-2 text-[13.5px]",
    row: "gap-2.5 px-1.5 py-2 text-[13px]",
    check: "size-4",
    empty: "text-[13px]",
  },
  sheet: {
    search: "py-2.5 text-[15px]",
    row: "min-h-[var(--tb-touch-min)] gap-3 px-1.5 py-2.5 text-[15px]",
    check: "size-[18px]",
    empty: "text-[14px]",
  },
} as const;

export function FacetPanel({
  facet,
  options,
  totalOptions,
  searchTerm,
  onSearchChange,
  onToggle,
  listClassName,
  density = "popover",
}: FacetPanelProps) {
  const measures = DENSITY[density];
  const searchId = useId();
  const visible = searchFacetOptions(options, searchTerm);
  const hasSearch = totalOptions > FACET_SEARCH_THRESHOLD;

  return (
    <>
      {hasSearch ? (
        <span className="relative flex items-center">
          <svg
            width="15"
            height="15"
            viewBox="0 0 24 24"
            fill="none"
            stroke="var(--fg-subtle)"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
            className="absolute left-0 top-1/2 -mt-[7px]"
          >
            <circle cx="11" cy="11" r="7" />
            <path d="m20 20-3.5-3.5" />
          </svg>
          <Input
            id={searchId}
            type="search"
            value={searchTerm}
            onChange={(event) => onSearchChange(event.target.value)}
            placeholder={facetSearchPlaceholder(facet)}
            aria-label={facetSearchPlaceholder(facet)}
            // `appearance-none`: a search field's native chrome made it 6px taller on a phone.
            // `leading` comes last: tailwind-merge drops a leading that a font size follows.
            className={`h-auto appearance-none border-b-[1px] pl-6 [&::-webkit-search-cancel-button]:hidden ${measures.search} leading-[normal]`}
          />
        </span>
      ) : null}

      {/*
       * `data-lenis-prevent` keeps the panel's smooth scroll from capturing the wheel inside the
       * list: without it, scrolling the list scrolls the page behind (criterion 22.4, §7.3).
       */}
      <div
        data-lenis-prevent=""
        className={`-m-1 flex flex-col gap-0.5 overflow-y-auto overscroll-contain p-1 ${listClassName}`}
      >
        {visible.map((option) => (
          <label
            key={option.value}
            className={`flex cursor-pointer items-center text-[var(--fg)] transition-colors duration-[var(--tb-duration-2)] ease-[var(--tb-ease-linen)] hover:bg-[var(--tb-porcelain-tint)] ${measures.row}`}
          >
            <Checkbox
              checked={option.isSelected}
              onCheckedChange={() => onToggle(option.value)}
              className={measures.check}
            />
            <span className="min-w-0 flex-1">{option.label}</span>
            <span className="font-mono text-[12px] tabular-nums text-[var(--fg-subtle)]">
              {option.count}
            </span>
          </label>
        ))}

        {visible.length === 0 ? (
          <p className={`p-1.5 text-[var(--fg-muted)] ${measures.empty}`}>Nada com esse nome.</p>
        ) : null}
      </div>
    </>
  );
}
